<script lang="ts" setup>
import { ContactHealthScoreModel } from '@/types';
import {
    getContactHealthScoreTrend,
    getContactHealthScoreStatus,
} from '@/utils/contactHealthScore';
import { computed } from 'vue';

interface Props {
    healthScore: ContactHealthScoreModel | null;
}

const props = defineProps<Props>();
const healthScore = computed(() => props.healthScore?.score ?? null);
const trendScore = computed(() => props.healthScore?.trend ?? null);
const circumference = 2 * Math.PI * 42;
const dash = computed(() =>
    healthScore.value !== null ? (healthScore.value / 100) * circumference : 0,
);

const scoreColor = (score: number) => {
    if (score >= 70) return 'text-success';
    if (score >= 45) return 'text-warning';
    return 'text-danger';
};
</script>
<template>
    <div class="flex flex-col gap-6 md:flex-row md:items-start">
        <div class="flex items-center gap-4">
            <div class="relative h-28 w-28">
                <svg viewBox="0 0 100 100" class="h-28 w-28 -rotate-90">
                    <circle
                        class="stroke-gray-200"
                        cx="50"
                        cy="50"
                        r="42"
                        fill="none"
                        stroke="var(--border)"
                        stroke-width="8"
                    />
                    <circle
                        v-if="healthScore !== null"
                        cx="50"
                        cy="50"
                        r="42"
                        fill="none"
                        :class="scoreColor(healthScore)"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-width="8"
                        :stroke-dasharray="`${dash} ${circumference}`"
                    />
                </svg>
                <div class="absolute inset-0 flex flex-col items-center justify-center">
                    <span class="text-foreground text-2xl font-semibold leading-none">{{
                        healthScore ?? '-'
                    }}</span>
                    <span class="text-muted-foreground mt-1 text-[10px] uppercase tracking-wider">
                        health
                    </span>
                </div>
            </div>
            <div>
                <p
                    v-if="healthScore !== null"
                    class="text-lg font-semibold"
                    :class="scoreColor(healthScore)"
                >
                    {{
                        $t(
                            'contact.timeline.stats.healthScore.' +
                                getContactHealthScoreStatus(healthScore),
                        )
                    }}
                </p>
                <p v-if="trendScore" class="text-xs text-gray-500">
                    {{ $t('contact.timeline.stats.healthScore.trend') }}:
                    <span class="font-medium text-gray-800">
                        {{
                            $t(
                                'contact.timeline.stats.healthScore.' +
                                    getContactHealthScoreTrend(trendScore),
                            )
                        }}
                    </span>
                </p>
            </div>
        </div>
    </div>
</template>
